/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {fromEvent, Subject} from 'rxjs';
import {debounceTime, map, startWith, takeUntil, tap} from 'rxjs/operators';

import {DATE_MAX, DATE_MIN} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {Employee} from '../../../shared/employees/employees.model';
import {ContractAlertsService} from '../../contract-alerts/contract-alerts.service';
import {ModalService} from '../../modal';
import {flatten} from '../../utils/arrays';
import {isPresent} from '../../utils/lang';
import {DataTransformer} from '../data-transformer/data-transformer';
import {FormCardFieldComponent} from '../form-card/form-card-field/form-card-field.component';
import {FormCardRowComponent} from '../form-card/form-card-row/form-card-row.component';
import {FormCardComponent} from '../form-card/form-card.component';
import {
  BlockingError,
  ErrorMessage,
  FormDescriptor,
  FormGeneratorError,
  FormGeneratorErrorDetail,
  FormValues,
  GroupZoneDescriptor,
  SimulationPatchCard,
  SimulationResponseByTab,
  TabSheetAndControlGroup,
  TabSheetDescriptor,
  Value,
  ZoneDescriptor,
  ZoneValues,
  ZoneValuesByGroup
} from '../form-generator.interfaces';
import {FormGeneratorService} from '../form-generator.service';
import {FormTabRowComponent} from '../form-tab/form-tab-row/form-tab-row.component';
import {FormTabComponent} from '../form-tab/form-tab.component';

@Component({
  selector: 'gp-generated-form',
  templateUrl: 'generated-form.component.html',
  styleUrls: ['./generated-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class GeneratedFormComponent extends Destroyable implements OnChanges, AfterViewInit, OnDestroy {

  @Input()
  public configuration: FormDescriptor = null;

  @Input()
  public values: FormValues = null;

  @Input()
  public activeCardSubject: Subject<boolean>;

  @Input()
  public initialTab: string = null;

  @Input()
  public isSkillModule = false;

  @Input()
  public isOnBoarding = false;

  @Input()
  public employee: Employee = null;

  @Input()
  public selectedOrg = null;

  @Input()
  public readOnly: boolean = false;

  @Input()
  public hiddenTabIds: string[] = [];

  @Output()
  public tabSelected: EventEmitter<TabSheetDescriptor> = new EventEmitter<TabSheetDescriptor>();

  @Output()
  public formLoaded: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('tabsContainer')
  public tabsContainer!: ElementRef;

  public form: UntypedFormGroup;

  public tabs: Array<ComponentRef<FormTabComponent>> = [];

  public zonesByTabs: any = {};

  public loading: boolean = true;

  // form will be set as loaded after a small timeout. Goal: to stabilise the form
  public loaded: boolean = false;

  public groupedCards: any = {};

  public blockingErrors: BlockingError[] = [];

  public errorMap = new Map<string, boolean>();

  public openDropdownTabId: string = null;
  public dropdownPosition: { top: number, left: number } = { top: 0, left: 0 };

  @ViewChild('tabPanes', {read: ViewContainerRef, static: true})
  private tabPanes: ViewContainerRef;
  public canScrollLeft = false;
  public canScrollRight = true;
  public scrolling = false;
  public isScrollingEnabled = false;

  constructor(private componentFactoryResolver: ComponentFactoryResolver,
              private formBuilder: UntypedFormBuilder,
              private modalService: ModalService,
              private alertsService: ContractAlertsService,
              private cd: ChangeDetectorRef,
              private formGeneratorService: FormGeneratorService) {
    super();
    this.form = formBuilder.group({});
  }

  public ngOnChanges(changes: any) {
    if (this.configuration === undefined) {
      return;
    }
    if (changes.values !== undefined) {
      this.form = this.formBuilder.group({});
      this.loaded = false;
      this.build().then(() => {
        setTimeout(() => {
          this.loaded = true;
          this.cd.markForCheck();
          this.formLoaded.emit();
        });
      });
    }
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.tabs.forEach((tabRef: ComponentRef<FormTabComponent>) => {
      tabRef.destroy();
      tabRef = null;
    });
    this.tabs = [];
  }

  /**
   * Activates a tab in the list of tabs this generated form has rendered
   * @param formTab the tab to activate
   */
  public activateTab(formTab: FormTabComponent) {
    const idx = this.tabs.findIndex(t => t.instance === formTab);
    this.tabs.forEach((tab: ComponentRef<FormTabComponent>) => tab.instance.active = false);
    formTab.active = true;
    this.tabSelected.emit(formTab.descriptor);
    this.formGeneratorService.currentTab$.next(formTab.descriptor.tabSheetID);
    this.cd.markForCheck();
    this.scrollToIndex(idx);
  }

  /**
   * Build the form based on current configuration object.
   *
   * @returns {Promise<void>} a promise that is resolved when the form has been built
   */
  public build(): Promise<UntypedFormGroup> {
    if (this.isReady()) {
      this.loading = true;

      // Reset tabs
      this.ngOnDestroy();

      // Construct form tabs according to configuration
      const sheets = this.configuration.tabSheets
        .sort((a: TabSheetDescriptor, b: TabSheetDescriptor) => a.tabSheetPosition - b.tabSheetPosition);

      return Promise.all(
        sheets.map((tabSheet: TabSheetDescriptor) => {
          const tabRef: ComponentRef<FormTabComponent> = this.tabPanes.createComponent(
            this.componentFactoryResolver.resolveComponentFactory(FormTabComponent));
          const tab: FormTabComponent = tabRef.instance;
          tab.descriptor = tabSheet;
          tab.data = this.values;
          tab.readOnly = this.readOnly;
          tab.currency = this.configuration.currencyString;
          tab.locale = this.configuration.localeString;
          tab.cliRegID = this.configuration.cliRegID;
          tab.groupedCards = this.groupedCards;
          tab.employee = this.employee;
          tab.isOnBoarding = this.isOnBoarding;
          tab.form = this.form;
          tab.activeCardSubject = this.activeCardSubject;
          tab.selectedOrg = this.selectedOrg;
          this.tabs.push(tabRef);
          tabRef.changeDetectorRef.detectChanges();

          if (tabSheet.groupZones) {
            tabSheet.groupZones.forEach(zone => {
              this.zonesByTabs[zone.groupZoneID] = tabSheet.tabSheetID;
            });
          }

          return new Promise((resolve, reject) => {
            tab.loaded
              .asObservable()
              .pipe(
                map(group => <TabSheetAndControlGroup>{
                  tabSheet: tabSheet,
                  controlGroup: group
                })
              )
              .subscribe(value => resolve(value));
          });
        })
      ).then((tabGroups: Array<TabSheetAndControlGroup>) => {
        tabGroups.forEach(item => this.form.registerControl(item.tabSheet.tabSheetID, item.controlGroup));

        if (this.tabs.length > 0) {
          if (isPresent(this.initialTab)) {
            const initialTab = this.tabs.map(tab => tab.instance)
              .find(tab => tab.descriptor.tabSheetID === this.initialTab);
            if (initialTab === undefined) {
              console.warn(`Initial tab [${this.initialTab}] not found in tab list`);
              this.activateTab(this.tabs[0].instance);
            } else {
              this.activateTab(initialTab);
            }
          } else {
            // Activate first tab when the form is loaded
            this.activateTab(this.tabs[0].instance);
          }
        }
        this.form.statusChanges.pipe(
          startWith(this.form.status),
          tap(() => this.setTabsError()),
          takeUntil(this.componentDestroyed$)
        ).subscribe();

        this.loading = false;
        return this.form;
      });
    } else {
      this.loading = false;
      return Promise.reject<UntypedFormGroup>('No configuration or values found');
    }
  }

  public checkBlockingError(): BlockingError[] {
    this.cd.detectChanges();
    this.blockingErrors = [];

    this.flatMapTabsUntilCardInstance()
      .forEach(card => {

        // TODO: harmonize form table and form card validation in order to make it easier

        // Form table
        if (card.errors && Object.keys(card.errors).length) {
          this.fillBlockingErrors(card, card.errors, card.errorMessages);
        }

        // Form card
        if (card.datesErrors && Object.keys(card.datesErrors).length) {
          this.fillBlockingErrors(card, card.datesErrors, card.datesErrorMessages);
        }

        if (card.groupErrors && Object.keys(card.groupErrors).length) {
          this.fillBlockingErrors(card, card.groupErrors, card.groupErrorMessages);
        }

        // Both
        if (card.zoneErrors && Object.keys(card.zoneErrors).length) {
          this.fillBlockingErrors(card, card.zoneErrors, card.zoneErrorMessages);
        }
      });

    return this.blockingErrors;
  }

  private fillBlockingErrors(componentInstance: any,
                             errorGroup: FormGeneratorError,
                             errorMessageGroup: ErrorMessage[]
  ) {

    Object.keys(errorGroup).forEach(key => {
      const error = errorGroup[key] as FormGeneratorErrorDetail;
      if (error.isBlocking) {
        this.blockingErrors.push({
          errorMessage: errorMessageGroup.find(errorMessage => errorMessage.text === key),
          tabSheetTransId: componentInstance.tabSheetID,
          groupZoneTransId: componentInstance.descriptor.transId
        });
      }
    });
  }

  public getModifiedCardsData(initialEmpDatas?: ZoneValues[], initialCardsValues?: ZoneValues[]): {
    [card: string]: ZoneValues[]
  } {
    this.cd.detectChanges();

    return this.flatMapTabsUntilCardInstance()
      .filter(card => {
        return this.isOnBoarding ? true : card.group.dirty;
      }).map(card => {
        if (this.isOnBoarding) {
          const initialCardData = initialCardsValues.filter(value => value.groupName === card.descriptor.groupZoneID);
          if (!this.isDataChanged(card.data, initialCardData)) {
            card.data = initialEmpDatas.filter(value => value.groupName === card.descriptor.groupZoneID);
          }
        }
        return card;
      })
      .reduce((memo, item: any) => {

        const datas = flatten([item.data, ...(memo[item.descriptor.groupZoneID] || [])]);

        // we consider that all modified cards data can't be default = true (because it has been modified)
        datas.forEach(data => data.default = false);

        this.formGeneratorService.transformZoneValuesList(datas);

        this.extractMaskBeforeUpdate(item.data, item.descriptor);

        if (item.descriptor.internalType === 'BANKING_DETAILS') {

          const keyZone: ZoneDescriptor = item.descriptor.zones
            .filter((zone: ZoneDescriptor) => zone.isKeyZone || zone.zoneID.includes('.paymentModality'))[0];

          datas.forEach(data => {
            const pmValue: string = data.values[keyZone.zoneID].value || item.initialData[0].values[keyZone.zoneID].value;

            data._groupName = `${data.groupName}_${pmValue}`;

            const newData = {};
            Object.keys(data.values).forEach((zone: string) => {
              newData[`${zone}_${pmValue}`] = data.values[zone];
            });
            data._values = newData;

            if (!memo[data._groupName]) {
              memo[data._groupName] = [];
            }
            memo[data._groupName].push(data);
          });
        } else {
          memo[item.descriptor.groupZoneID] = datas;
        }

        if (item.descriptor.isDated && datas.length === 1) {
          const isDeleted: boolean = !Object.keys(datas[0].values)
              .some(key => datas[0].values[key].value !== undefined)
            && datas[0].begindate === DATE_MIN
            && datas[0].enddate === DATE_MAX;

          if (isDeleted) {
            datas[0].values = {};
          }
        }
        return memo;
      }, {});
  }

  private flatMapTabsUntilCardInstance(): any[] {
    return this.tabs.map(tab => tab.instance)
      .flatMap(tab => tab.rows)
      .map((row: ComponentRef<FormTabRowComponent>) => row.instance)
      .flatMap(cardRow => cardRow.cards)
      .map((card: ComponentRef<any>) => card.instance);
  }

  public getAutoCalculatedZonesByGroup(): { [groupZoneID: string]: Set<string> } {
    return this.getZonesByGroup((field: FormCardFieldComponent) => field?.canCalculate && !field.isHeritageRemoved);
  }

  public getManuallyOverriddenZonesByGroup(): { [groupZoneID: string]: Set<string> } {
    return this.getZonesByGroup((field: FormCardFieldComponent) => field?.canCalculate && field.isHeritageRemoved);
  }

  private getZonesByGroup(fieldPredicate: (field: FormCardFieldComponent) => boolean): { [groupZoneID: string]: Set<string> } {
    const result: { [groupZoneID: string]: Set<string> } = {};

    this.flatMapTabsUntilCardInstance().forEach((card: FormCardComponent) => {
      const groupZoneID: string = card?.descriptor?.groupZoneID;
      if (!groupZoneID) {
        return;
      }

      const zoneIds: string[] = (card.fieldRows ?? [])
        .flatMap((rowRef: ComponentRef<FormCardRowComponent>) => rowRef.instance?.fields ?? [])
        .map((fieldRef: ComponentRef<FormCardFieldComponent>) => fieldRef.instance)
        .filter(fieldPredicate)
        .map((field: FormCardFieldComponent) => field.descriptor?.zoneID)
        .filter((zoneId: string): zoneId is string => Boolean(zoneId));

      if (zoneIds.length) {
        result[groupZoneID] = new Set([...(result[groupZoneID] ?? []), ...zoneIds]);
      }
    });

    return result;
  }

  private isReady(): boolean {
    return isPresent(this.configuration) && isPresent(this.values);
  }

  /**
   * This method extract mask (if exist) from each data values when we do an update before sending them to BE
   */

  private extractMaskBeforeUpdate(datas: ZoneValues[], descriptor: GroupZoneDescriptor) {
    datas.forEach(data => {
      const keys = Object.keys(data.values);
      keys.forEach(key => {
        const mask = descriptor.zones.find(zone => zone.zoneID === key)
          ? descriptor.zones.find(zone => zone.zoneID === key).inputMask
          : undefined;
        data.values[key].value = mask
          ? DataTransformer.extractMask(data.values[key].value, mask)
          : data.values[key].value;
      });
    });
  }

  private isDataChanged(cardData: ZoneValues[], initialCardData: ZoneValues[]) {
    if (cardData.length !== initialCardData.length) {
      return true;
    } else {
      for (let i = 0; i < cardData.length; i++) {
        const dataValues: Value[] = Object.values(cardData[i].values);
        const initialValues: Value[] = Object.values(initialCardData[i].values);

        if (dataValues.length !== initialValues.length ||
          dataValues.some((dataValue, index) => !initialValues[index] || dataValue.value !== initialValues[index].value)) {
          return true;
        }
      }
      return false;
    }
  }

  public redirectToTab(tabId: string, groupZoneID?: string) {
    const tab = this.tabs.find(t => t.instance.descriptor.tabSheetID === tabId);
    if (!tab) {
      console.warn(`redirectToTab: tab [${tabId}] not found in tab list`);
      return;
    }
    this.activateTab(tab.instance);

    if (groupZoneID) {
      this.cd.detectChanges();
      this.scrollToGroupZoneById(groupZoneID);
    }
  }

  public toggleTabDropdown(event: Event, tabId: string): void {
    event.stopPropagation();
    if (this.openDropdownTabId === tabId) {
      this.openDropdownTabId = null;
    } else {
      const target = event.target as HTMLElement;
      const link = target.closest('.nav-link') || target;
      const rect = link.getBoundingClientRect();
      // A position:fixed element is positioned relative to the viewport, UNLESS one of its
      // ancestors establishes a containing block (transform/filter/perspective/will-change).
      // This is the case when the form is displayed inside a modal, which applies a transform
      // for centering/animation. In that case the viewport coordinates returned by
      // getBoundingClientRect() must be made relative to that ancestor, otherwise the dropdown
      // appears offset and not under its tab.
      const containingBlock = this.getFixedContainingBlock(link as HTMLElement);
      const offset = containingBlock
        ? containingBlock.getBoundingClientRect()
        : { top: 0, left: 0 };
      this.dropdownPosition = { top: rect.bottom - offset.top, left: rect.left - offset.left };
      this.openDropdownTabId = tabId;
    }
    this.cd.markForCheck();
  }

  /**
   * Walks up the ancestor chain to find the element that establishes the containing block for
   * position:fixed descendants (i.e. an ancestor with a transform, perspective, filter or
   * will-change). Returns null when no such ancestor exists and the viewport is the containing
   * block (the regular, non-modal case).
   */
  private getFixedContainingBlock(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      if (
        (style.transform && style.transform !== 'none') ||
        (style.perspective && style.perspective !== 'none') ||
        (style.filter && style.filter !== 'none') ||
        style.willChange === 'transform' ||
        style.willChange === 'perspective' ||
        style.willChange === 'filter'
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  public scrollToGroupZone(event: Event, tab: FormTabComponent, groupZoneID: string): void {
    event.stopPropagation();
    this.openDropdownTabId = null;

    // Activate tab if not already active
    if (!tab.active) {
      this.activateTab(tab);
    }
    this.cd.detectChanges();

    this.scrollToGroupZoneById(groupZoneID);
  }

  private scrollToGroupZoneById(groupZoneID: string): void {
    // Scroll to the group zone element after DOM is updated
    setTimeout(() => {
      requestAnimationFrame(() => {
        const contentEl = document.querySelector('.tab-content') as HTMLElement;
        const el = (contentEl ?? document).querySelector(`[test-id="${groupZoneID}"]`) as HTMLElement;
        if (!el) { return; }

        // Use scrollIntoView with a manual offset for the sticky tabs
        const tabsEl = document.querySelector('.scrollable-tabs-container') as HTMLElement;
        const tabsHeight = tabsEl ? tabsEl.offsetHeight : 0;

        // Find the scrollable ancestor (must actually be able to scroll vertically)
        let scrollParent = el.parentElement;
        while (scrollParent) {
          const style = window.getComputedStyle(scrollParent);
          if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && scrollParent.scrollHeight > scrollParent.clientHeight) {
            break;
          }
          scrollParent = scrollParent.parentElement;
        }

        if (scrollParent) {
          const elTop = el.getBoundingClientRect().top;
          const scrollParentTop = scrollParent.getBoundingClientRect().top;
          scrollParent.scrollTop += (elTop - scrollParentTop - tabsHeight);
        } else {
          // Scroll using window with offset for the sticky tabs
          const elRect = el.getBoundingClientRect();
          const scrollOffset = elRect.top + window.scrollY - tabsHeight;
          window.scrollTo({top: scrollOffset, behavior: 'smooth'});
        }
      });
    }, 150);
  }

  public getUniqueGroupZones(groupZones: GroupZoneDescriptor[]): GroupZoneDescriptor[] {
    const seen = new Set<string>();
    return groupZones.filter(gz => {
      const key = gz.groupZoneID.includes(':') ? gz.groupZoneID.split(':')[0] : gz.groupZoneID;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  public getUniqueGroupZoneCount(groupZones: GroupZoneDescriptor[]): number {
    return this.getUniqueGroupZones(groupZones).length;
  }

  public isGroupZoneInError(tabId: string, groupZoneID: string): boolean {
    const tabGroup = this.form?.controls[tabId] as UntypedFormGroup;
    if (!tabGroup || !tabGroup.controls) {
      return false;
    }
    const baseKey = groupZoneID.includes(':') ? groupZoneID.split(':')[0] : groupZoneID;
    return Object.keys(tabGroup.controls).some(key => {
      const controlBaseKey = key.includes(':') ? key.split(':')[0] : key;
      return controlBaseKey === baseKey && tabGroup.controls[key]?.invalid;
    });
  }

  public closeDropdown(): void {
    if (this.openDropdownTabId) {
      this.openDropdownTabId = null;
      this.cd.markForCheck();
    }
  }

  public scrollTabs(direction: 'left' | 'right') {
    if (this.scrolling || !this.canScrollRight && direction === 'right') {
      return;
    }

    this.scrolling = true;
    const scrollAmount = 1000;
    const duration = 1000;
    const tabsContainer = this.tabsContainer.nativeElement;
    const startLeft = tabsContainer.scrollLeft;
    const targetLeft = direction === 'left'
      ? Math.max(0, startLeft - scrollAmount)
      : Math.min(tabsContainer.scrollWidth - tabsContainer.clientWidth, startLeft + scrollAmount);

    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 0.5 * (1 - Math.cos(Math.PI * progress));

      tabsContainer.scrollLeft = startLeft + (targetLeft - startLeft) * easedProgress;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        this.scrolling = false;
        this.updateScrollState();
      }
    };

    requestAnimationFrame(animateScroll);
  }

  public scrollToIndex(idx: number): void {
    if (idx !== undefined) {
      const items = this.tabsContainer?.nativeElement?.querySelectorAll('a.nav-link');
      const item  = items[idx] as HTMLElement;
      if (!item) { return; }

      const listRect = this.tabsContainer.nativeElement.getBoundingClientRect();
      const linkRect = item.getBoundingClientRect();
      const target = item.offsetLeft
        + linkRect.width / 2
        - listRect.width / 2;

      setTimeout(() => {
        this.tabsContainer.nativeElement.scrollTo({ left: target, behavior: 'smooth' });
        setTimeout(() => {
          this.updateScrollState();
        }, 500);
      });
    }
  }

  private updateScrollState() {
    const tabsElement = this.tabsContainer.nativeElement;
    this.canScrollLeft = tabsElement.scrollLeft > 0;
    this.canScrollRight = tabsElement.scrollLeft + tabsElement.clientWidth + 1 < tabsElement.scrollWidth;
    this.cd.markForCheck();
  }

  private checkScrollNeeded(): void {
    const tabsElement = this.tabsContainer?.nativeElement;
    if (!tabsElement) {
      return;
    }

    this.isScrollingEnabled = tabsElement.scrollWidth > tabsElement.clientWidth;
    this.updateScrollState();
  }


  public ngAfterViewInit() {
    this.checkScrollNeeded();

    fromEvent(window, 'resize')
      .pipe(
        debounceTime(300),
        tap(() => this.checkScrollNeeded()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }


  public setTabsError() {
    Object.entries(this.form.controls).forEach(([key, control]) => {
      if (control.status === 'INVALID') {
        this.errorMap.set(key, true);
      } else if (control.status === 'VALID') {
        this.errorMap.set(key, false);
      }
      if (this.configuration?.tabSheets.find(tab => tab.tabSheetID === key)?.error) {
        this.errorMap.set(key, true);
      }
    });
    this.cd.markForCheck();
  }

  public simulationFill(response: SimulationResponseByTab): void {
    const cards = this.flatMapTabsUntilCardInstance();
    const manualZonesByGroup = this.getManuallyOverriddenZonesByGroup();
    for (const [tabName, control] of Object.entries(this.form.controls)) {
      const tabCards = cards.filter(card => card.tabSheetID === tabName);
      this.patchTab(control, response[tabName], tabCards, manualZonesByGroup);
    }
  }

  private patchTab(
    control: AbstractControl,
    data: ZoneValuesByGroup | undefined,
    cards: SimulationPatchCard[] = [],
    manualZonesByGroup: { [groupZoneID: string]: Set<string> } = {}
  ): void {
    if (!data) {
      return;
    }

    Object.entries(data)
      .filter(([, zoneValues]) => zoneValues?.length)
      .forEach(([key, zoneValues]) => {
        const targetControl = this.resolveTargetControl(control, key);

        if (!targetControl) {
          return;
        }

        // For dated groups with multiple entries, only apply values for the currently active dated zone.
        // Without this, the last entry in the array would overwrite values for the wrong dated period.
        let valuesToPatch: Array<Record<string, Value>>;
        if (zoneValues.length > 1) {
          const matchingCard = cards.find(card => card.descriptor?.groupZoneID === key);
          const activeBeginDate = matchingCard?.activeEntity?.begindate;
          const activeEndDate = matchingCard?.activeEntity?.enddate;

          const matchingEntry = activeBeginDate && activeEndDate
            ? zoneValues.find(entry => entry.begindate === activeBeginDate && entry.enddate === activeEndDate)
            : null;

          valuesToPatch = matchingEntry
            ? [matchingEntry.values].filter((v): v is Record<string, Value> => Boolean(v))
            : zoneValues.map(v => v.values).filter((v): v is Record<string, Value> => Boolean(v));
        } else {
          valuesToPatch = zoneValues.map(v => v.values).filter((v): v is Record<string, Value> => Boolean(v));
        }

        const patch = this.buildPatch(
          (targetControl.value ?? {}) as Record<string, unknown>,
          valuesToPatch,
          manualZonesByGroup[key]
        );

        if (!Object.keys(patch).length) {
          return;
        }

        targetControl.patchValue(patch, { emitEvent: false });
      });
  }

  private resolveTargetControl(
    control: AbstractControl,
    wrapperKey: string | null
  ): AbstractControl | null {
    if (!wrapperKey) {
      return control;
    }

    return control.get(wrapperKey) ?? control;
  }

  private buildPatch(
    targetValue: Record<string, unknown>,
    values: Array<Record<string, Value>>,
    skipZones: Set<string> = new Set<string>()
  ): Record<string, unknown> {
    const patch: Record<string, unknown> = {};

    for (const value of values) {
      for (const key of Object.keys(targetValue)) {
        if (skipZones.has(key)) {
          continue;
        }
        if (key in value) {
          patch[key] = this.extractPatchValue(value[key]);
        }
      }
    }

    return patch;
  }

  private extractPatchValue(value: { content?: Value; value?: unknown } | null | undefined): unknown {
    if (value?.content?.value !== undefined) {
      return value.content.value;
    }
    if (value?.value !== undefined) {
      return value.value;
    }
    return undefined;
  }



}
